import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * One page's scope selection. Legal entities picked here mean "the whole entity";
 * branches mean "just that branch". The two are alternatives, not a narrowing — the
 * backend ORs them — so a selection may hold all of entity A plus one branch of entity B.
 *
 * Empty on both axes means "everything I can see", which is the default.
 */
export interface ScopeSelection {
  legalEntityIds: string[];
  branchIds: string[];
}

export const EMPTY_SCOPE: ScopeSelection = { legalEntityIds: [], branchIds: [] };

/** Identifies whose scope is whose in {@link SettingsState.scopes}. */
export type ScopePageKey =
  | "properties"
  | "contacts"
  | "leads"
  | "diary"
  | "users"
  | "campaigns"
  | "labels";

interface SettingsState {
  /**
   * The organisation context: which account the console is working in. Set by the
   * account switcher (master users) or auto-selected from the profile, and used for
   * everything that needs a single entity — profile fetches, forms, drawers,
   * integrations. Not a list filter; that is {@link SettingsState.scopes}.
   */
  legalEntityId: string | null;
  legalEntityName: string | null;
  /**
   * Set only when the signed-in user holds exactly one branch, in which case every
   * request is pinned to it. No longer user-selectable — the per-page scope picker
   * replaced the global branch dropdown.
   */
  branchId: string | null;
  branchName: string | null;
  /**
   * Scope picker selections, one per page. Kept apart rather than shared: filtering
   * Properties down to one branch should not silently filter Leads too, which is what a
   * single shared selection did.
   */
  scopes: Partial<Record<ScopePageKey, ScopeSelection>>;
  /** id → display name for everything selected anywhere, so chips survive a reload. */
  scopeLabels: Record<string, string>;
  setLegalEntity: (id: string | null, name: string | null) => void;
  setBranch: (id: string | null, name: string | null) => void;
  setScope: (page: ScopePageKey, scope: ScopeSelection, labels: Record<string, string>) => void;
  clearScope: (page: ScopePageKey) => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      legalEntityId: null,
      branchId: null,
      legalEntityName: null,
      branchName: null,
      scopes: {},
      scopeLabels: {},
      setLegalEntity: (id, name) =>
        set({
          legalEntityId: id,
          legalEntityName: name,
          branchId: null,
          branchName: null,
          // Switching organisation invalidates every id in every selection.
          scopes: {},
          scopeLabels: {},
        }),
      setBranch: (id, name) => set({ branchId: id, branchName: name }),
      setScope: (page, scope, labels) =>
        set((state) => ({
          scopes: { ...state.scopes, [page]: scope },
          scopeLabels: { ...state.scopeLabels, ...labels },
        })),
      clearScope: (page) =>
        set((state) => ({ scopes: { ...state.scopes, [page]: EMPTY_SCOPE } })),
    }),
    {
      name: "atlas-settings",
    }
  )
);

/**
 * One page's scope selection. Returns the shared {@link EMPTY_SCOPE} constant when the
 * page has none, so the identity stays stable across renders and callers can use it as a
 * hook dependency directly.
 */
export function usePageScope(page: ScopePageKey): ScopeSelection {
  return useSettingsStore((s) => s.scopes[page] ?? EMPTY_SCOPE);
}
